import {applyPatch, reversePatch, type StructuredPatch} from 'diff';
import {useEffect, useReducer} from 'react';

import {
  cachedLoadArtifactFileContent,
  cachedLoadArtifactManifest,
} from '../../../files';
import {useApolloClient} from '../../../state/hooks';
import {locateFileInManifest} from './fileLocator';
import {getFileName, isDeletedFile} from './patchFileUtils';

// ----
// Begin state machine code. There are a number of state transitions that need
// to be captured to handle the various loading/error/success states of this
// hook, so a reducer seemed appropriate to keep the logic clear and
// maintainable.
//
//   (init)                       success
//    ──────► loading ──────────────────────────────────► ok ──┐
//                │                                            │
//                │ not-found / fetch-failed /           reset │
//                │ apply-failed / unknown-error               ▼
//                ▼                                        loading
//             error
//                │ reset
//                ▼
//             loading
//
// ----
type PatchFileAction =
  | {type: 'not-found'; content: string; error: string}
  | {type: 'fetch-failed'; content: string; error: string}
  | {type: 'apply-failed'; content: string; error: string}
  | {type: 'unknown-error'; content: string; error: string}
  | {type: 'success'; originalContent: string; modifiedContent: string}
  | {type: 'reset'};

export type PatchFileState =
  | {
      status: 'loading';
    }
  | {
      status: 'ok';
      originalContent: string;
      modifiedContent: string;
    }
  | {
      status: 'error';
      reason: 'apply-failed' | 'not-found' | 'fetch-failed' | 'unknown-error';
      content: string;
      error: string;
    };

function patchFileReducer(
  _state: PatchFileState,
  action: PatchFileAction
): PatchFileState {
  switch (action.type) {
    case 'not-found':
      return {
        status: 'error',
        reason: 'not-found',
        content: action.content,
        error: action.error,
      };
    case 'fetch-failed':
      return {
        status: 'error',
        reason: 'fetch-failed',
        content: action.content,
        error: action.error,
      };
    case 'apply-failed':
      return {
        status: 'error',
        reason: 'apply-failed',
        content: action.content,
        error: action.error,
      };
    case 'unknown-error':
      return {
        status: 'error',
        reason: 'unknown-error',
        content: action.content,
        error: action.error,
      };
    case 'success': {
      return {
        status: 'ok',
        originalContent: action.originalContent,
        modifiedContent: action.modifiedContent,
      };
    }
    case 'reset':
      return {status: 'loading'};
  }
}
// ----
// End state machine code.
// ----

// Custom error classes to distinguish between different failure modes when
// loading/applying the patch. These are caught in the promise chain and used
// to dispatch different error states to the reducer, which allows the UI to
// show different messages for "file not found in artifact" vs
// "failed to fetch file content" etc...
class FileNotFound extends Error {}
class FetchFailed extends Error {}
class ErrorApplyingPatch extends Error {}

/**
 * Resolves `path` in the artifact manifest and fetches its content.
 * Throws `FileNotFound` if the path isn't in the manifest, or `FetchFailed`
 * if the content can't be retrieved.
 */
async function getManifestFile(
  client: ReturnType<typeof useApolloClient>,
  path: string,
  artifactId: string
) {
  const manifest = await cachedLoadArtifactManifest(client, artifactId);

  const manifestPath = locateFileInManifest(manifest, path);
  if (!manifestPath) {
    throw new FileNotFound(`File ${path} not found in artifact ${artifactId}`);
  }
  const file = await cachedLoadArtifactFileContent(
    client,
    artifactId,
    manifestPath
  );
  if (!file.contents) {
    throw new FetchFailed(
      `Failed to fetch file content for ${path} in artifact ${artifactId}`
    );
  }
  return file.contents;
}

/**
 * Fetches the modified file content from the artifact (empty string for
 * deleted files), then reverses and applies the patch to reconstruct the
 * original content. Returns `[originalContent, modifiedContent]`.
 */
async function loadFile(
  client: ReturnType<typeof useApolloClient>,
  patch: StructuredPatch,
  artifactId: string
) {
  const path = getFileName(patch);
  const file = isDeletedFile(patch)
    ? ''
    : await getManifestFile(client, path, artifactId);

  const reversed = reversePatch(patch);
  const originalContent = applyPatch(file, reversed);
  if (originalContent === false) {
    throw new ErrorApplyingPatch(
      `Failed to apply patch to file ${path} in artifact ${artifactId}`
    );
  }

  // Return type: [BEFORE_PATCH_STR, AFTER_PATCH_STR]
  return [originalContent, file];
}

/**
 * Loads before/after file contents for a structured patch so a diff viewer
 * can display them. For deleted files, the modified content is an empty string
 * rather than fetched from the artifact.
 *
 * @param artifactId - The artifact to load the file from.
 * @param patch - The structured patch describing changes to the file.
 * @returns Loading/error/success state. On success, contains both the original
 *   and modified file contents as strings.
 */
export function usePatchFile(
  artifactId: string,
  patch: StructuredPatch
): PatchFileState {
  const client = useApolloClient();
  const [state, dispatch] = useReducer(patchFileReducer, {status: 'loading'});

  useEffect(() => {
    let mounted = true;

    loadFile(client, patch, artifactId).then(
      ([originalContent, modifiedContent]) => {
        if (!mounted) {
          // This useEffect or the component has been unmounted since we started
          // this loading process. If that's the case, we don't want to call
          // dispatch, because that will either load stale data, or will trigger
          // a react "setState on unmounted component" warning.
          return;
        }
        dispatch({
          type: 'success',
          originalContent,
          modifiedContent,
        });
      },
      error => {
        if (!mounted) {
          return;
        }

        // If the patch application fails, we want the raw patch lines so we
        // can fall back to showing that to the user, along with an error
        // message.
        const content = patch.hunks.flatMap(h => h.lines).join('\n');
        if (error instanceof FileNotFound) {
          dispatch({type: 'not-found', content, error: error.message});
        } else if (error instanceof FetchFailed) {
          dispatch({type: 'fetch-failed', content, error: error.message});
        } else if (error instanceof ErrorApplyingPatch) {
          dispatch({type: 'apply-failed', content, error: error.message});
        } else {
          dispatch({
            type: 'unknown-error',
            content,
            error: 'An unhandled error occurred',
          });
        }
      }
    );

    return () => {
      dispatch({type: 'reset'});
      mounted = false;
    };
  }, [artifactId, client, patch]);

  return state;
}
